import type { VerseTopic } from "./types";

export const topic: VerseTopic = {
  slug: "bible-verses-about-gods-promises",
  title: "8 Bible verses about God's promises, and who each was made to",
  h1: "Bible verses about God's promises",
  description:
    "Eight passages from the World English Bible with a note on each — because most disappointment with a promise comes from claiming one that was addressed to somebody else.",
  targets: [
    "bible verses about god's promises",
    "scripture about promises of god",
    "verses on god keeping his promises",
  ],
  published: "2026-09-03",
  standfirst:
    "The question that decides whether a promise holds is not how much you believe it. It is who it was made to, and about what.",
  takeaways: [
    "Most promises in Scripture are addressed to a specific person or nation about a specific thing, and the ones that transfer do so because the New Testament says they transfer.",
    "2 Corinthians 1:20 is the verse that governs the rest: the promises find their yes in Christ, which is the New Testament's own rule for how they carry over.",
    "Numbers 23:19 is about God's character rather than about any particular outcome, which is why it survives a promise you thought you had not being kept.",
    "A promise you have to keep defending after every funeral was probably not addressed to you. That is not a loss of faith; it is reading.",
  ],
  intro: [
    {
      type: "p",
      text: "God's promises is one of the most-searched topics on the largest topical Bible site, and the search behind it is usually specific: somebody has been told to claim a promise and wants to know which ones there are. The passages below are the ones that keep coming up, and each note says who the promise was made to — which is the part that decides whether it is yours.",
    },
    {
      type: "p",
      text: "That question is not pedantry. Nearly every collapse of confidence in this area traces back to a promise being claimed out of its address — the classic case being {Jeremiah 29:11}, written to deportees about a seventy-year exile and printed on graduation cards.",
      links: { "Jeremiah 29:11": "/verse/jeremiah-29-11" },
    },
    {
      type: "note",
      text: "Quotations are from the World English Bible, which is in the public domain.",
    },
  ],
  verses: [
    {
      verse: {
        text: 'For however many are the promises of God, in him is the "Yes." Therefore also through him is the "Amen", to the glory of God through us.',
        ref: "2 Corinthians 1:20",
      },
      note: "This is the verse that governs the others, and it should be read first. Paul is defending himself against a charge of being unreliable — he changed a travel plan — and his answer is that God's promises are not a mixture of yes and no. The claim is that they converge on one person rather than being distributed as a list to be claimed individually, which is the New Testament's own account of how the older promises carry over.",
    },
    {
      verse: {
        text: "God is not a man, that he should lie, nor a son of man, that he should repent. Has he said, and he won't do it? Or has he spoken, and he won't make it good?",
        ref: "Numbers 23:19",
      },
      note: "Said, remarkably, by a foreign diviner who had been hired to curse Israel and could not manage it. It is a statement about character rather than about any particular outcome, which is what makes it the most durable verse in this collection: it is still true on the day a specific hope does not arrive, and it is the ground for asking what you had actually been promised.",
    },
    {
      verse: {
        text: "Nothing failed of any good thing which Yahweh had spoken to the house of Israel. All came to pass.",
        ref: "Joshua 21:45",
      },
      note: "A summary sentence at the end of the land allocation, and it is worth knowing that the book of Judges immediately afterwards describes the same territory being lost repeatedly. The two are not in contradiction: the promise concerned what God did, and Judges concerns what the people then did with it. Read together they are more useful than either alone.",
    },
    {
      verse: {
        text: "let's hold fast the confession of our hope without wavering; for he who promised is faithful.",
        ref: "Hebrews 10:23",
      },
      note: "The instruction is to hold on, and the reason given is not that the holding on is easy or that the outcome is near. It is a fact about the other party. Hebrews is written to people considering going back to something safer, and the whole letter assumes the wait has been long enough to be a real question.",
    },
    {
      verse: {
        text: "so is my word that goes out of my mouth: it will not return to me void, but it will accomplish that which I please, and it will prosper in the thing I sent it to do.",
        ref: "Isaiah 55:11",
      },
      note: "Frequently quoted as a guarantee that a Bible verse you have spoken over a situation will produce a result. The passage is about God's own word achieving God's own purposes — which the next clause says plainly, twice. It is a strong statement and it is about his intention rather than about yours, and that distinction is the difference between confidence and superstition.",
    },
    {
      verse: {
        text: "by which he has granted to us his precious and exceedingly great promises; that through these you may become partakers of the divine nature, having escaped from the corruption that is in the world by lust.",
        ref: "2 Peter 1:4",
      },
      note: "Notice what the promises are for in this sentence: becoming a particular kind of person, and getting out from under something. Not circumstances. It is one of the few places that states the purpose of the promises directly, and the purpose named is character rather than provision — which reframes the whole category.",
    },
    {
      verse: {
        text: "Yahweh himself is who goes before you. He will be with you. He will not fail you nor forsake you. Don't be afraid. Don't be discouraged.",
        ref: "Deuteronomy 31:8",
      },
      note: "Said by Moses to Joshua in front of the whole assembly, about a specific job neither of them could have done alone. The promise is presence rather than success, and the two commands attached — do not be afraid, do not be discouraged — concede that both were live possibilities. Hebrews 13:5 later applies the same sentence to Christians generally, which is the New Testament doing the transferring rather than a reader assuming it.",
    },
    {
      verse: {
        text: "Be strong and courageous. Don't be afraid or scared of them, for Yahweh your God himself is who goes with you. He will not fail you nor forsake you.",
        ref: "Deuteronomy 31:6",
      },
      note: "Two verses earlier, addressed to the people rather than to Joshua, and worth having alongside its neighbour because the repetition is deliberate. The same promise is given to the leader and to everybody else, which in an ancient near-eastern setting is unusual: divine accompaniment was normally a royal privilege, and here it is issued to the whole camp.",
    },
  ],
  pray: [
    {
      type: "h2",
      id: "how-to-use-these",
      text: "How to use these",
    },
    {
      type: "p",
      text: "Ask two questions of any promise before claiming it: who was it said to, and what was it about. Most of the time the answer is encouraging — the New Testament transfers a great deal explicitly. Occasionally it is not, and finding that out in advance is far better than finding it out in a hospital.",
    },
    {
      type: "p",
      text: "And keep Numbers 23:19 for the days when something you were counting on does not happen. It is a claim about character rather than outcome, which means it is still standing when a particular hope is not — the same shape as {a prayer that asks for the day rather than the season}.",
      links: {
        "a prayer that asks for the day rather than the season":
          "/prayers/prayer-for-strength",
      },
    },
    {
      type: "note",
      text: "A promise you cannot say out loud to somebody in the worst week of their life was probably being used as a slogan rather than read.",
    },
  ],
  faq: [
    {
      q: "How many promises are in the Bible?",
      a: "Counts range from a few hundred to several thousand depending on what is counted as a promise, and none of the figures is authoritative. The more useful question is which promises were addressed to whom, which is what these notes answer.",
    },
    {
      q: "How do I know if a Bible promise applies to me?",
      a: "Ask who it was said to and what it was about. Many transfer, and the New Testament usually says so explicitly when they do — Hebrews 13:5 taking up Deuteronomy 31:6 is a clear example.",
    },
    {
      q: "Which translation is quoted here?",
      a: "The World English Bible, which is in the public domain, so all eight passages are printed whole rather than referred to. It renders the divine name as Yahweh, which is why Joshua 21:45 and Deuteronomy 31:6 read differently here from the versions that print 'the LORD'.",
    },
  ],
  related: ["how-to-memorize-bible-verses", "bible-in-a-year-on-your-phone"],
  relatedTopics: ["bible-verses-about-strength", "bible-verses-about-peace"],
};
